/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useMutation } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { SplitButton } from '@atlaskit/button/split-button/split-button';
import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import { useFlags } from '@atlaskit/flag/use-flags';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import StatusErrorIcon from '@atlaskit/icon/core/cross-circle';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
} from '@atlassian/analytics-bridge/fire-analytics/fire-analytics';
import { FinishMigrationReviewModal } from '@atlassian/company-hub-common/finish-migration-review-modal';
import { getMigrationPublishedUrl } from '@atlassian/company-hub-common/migration-publish-success';
import { PublishMigrationModal } from '@atlassian/company-hub-common/publish-migration-modal';
import type { MigrationReview } from '@atlassian/company-hub-common/types';

import type { migrationReviewActionBarGoLiveHelpCenterMutation } from './__generated__/migrationReviewActionBarGoLiveHelpCenterMutation.graphql';
import messages from './messages';

const styles = cssMap({
	container: {
		paddingBlockEnd: token('space.150'),
		paddingBlockStart: token('space.150'),
		paddingInlineEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
	},
});

type PublishAttempt = {
	disposable: { dispose: () => void } | null;
	retry: boolean;
	startedAt: number;
	terminal: boolean;
};

type PublishAttemptState = {
	attempt: PublishAttempt | null;
	retry: boolean;
};

type UIEvent = {
	action: 'clicked' | 'viewed';
	actionSubject: 'button' | 'dropdownItem' | 'migrationReviewActionBar' | 'modal';
	actionSubjectId: string;
};

export const MigrationReviewActionBar = ({
	cloudId,
	helpCenterAri,
	helpCenterName,
	migrationReview,
	publishedHelpCenterUrl,
}: {
	cloudId: string;
	helpCenterAri: string;
	helpCenterName?: string;
	migrationReview: MigrationReview;
	publishedHelpCenterUrl: string;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlags();
	const [isFinishLaterModalOpen, setIsFinishLaterModalOpen] = React.useState(false);
	const [isPublishModalOpen, setIsPublishModalOpen] = React.useState(false);
	const hasTrackedView = React.useRef(false);
	const publishAttemptRef = React.useRef<PublishAttemptState>({ attempt: null, retry: false });
	const [commitGoLiveHelpCenter, isPublishing] =
		useMutation<migrationReviewActionBarGoLiveHelpCenterMutation>(graphql`
			mutation migrationReviewActionBarGoLiveHelpCenterMutation(
				$cloudId: ID!
				$input: HelpCenterHubGoLiveHelpCenterInput!
			) {
				helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
					goLiveHelpCenter(input: $input) {
						success
						errors {
							message
						}
					}
				}
			}
		`);

	const fireAnalyticsEvent = React.useCallback(
		({ action, actionSubject, actionSubjectId }: UIEvent) => {
			fireUIAnalytics(createAnalyticsEvent({ action, actionSubject }), actionSubjectId);
		},
		[createAnalyticsEvent],
	);

	React.useEffect(() => {
		if (hasTrackedView.current) {
			return;
		}

		hasTrackedView.current = true;
		fireAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'migrationReviewActionBar',
			actionSubjectId: 'migrationReviewActionBar',
		});
	}, [fireAnalyticsEvent]);

	const showPublishErrorFlag = React.useCallback(() => {
		const title = formatMessage(messages.publishError);
		showFlag({
			title,
			isAutoDismiss: true,
			icon: <StatusErrorIcon label={title} color={token('color.icon.danger')} spacing="spacious" />,
		});
	}, [formatMessage, showFlag]);

	const completePublishAttempt = React.useCallback(
		(
			attempt: PublishAttempt,
			action: 'cancelled' | 'failed' | 'succeeded',
			outcome:
				| { errorCode: 'EMPTY_RESPONSE' | 'PUBLISH_REJECTED' | 'RELAY_REQUEST_FAILED' }
				| { outcome: 'published' | 'unmounted' },
		) => {
			const state = publishAttemptRef.current;
			if (state.attempt !== attempt || attempt.terminal) {
				return false;
			}

			attempt.terminal = true;
			state.retry = action === 'failed';
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				`helpCenterMigrationReviewAction ${action}`,
				'goLiveHelpCenter',
				{
					duration: Date.now() - attempt.startedAt,
					retry: attempt.retry,
					...outcome,
				},
			);

			return true;
		},
		[createAnalyticsEvent],
	);

	React.useEffect(
		() => () => {
			const attempt = publishAttemptRef.current.attempt;
			if (!attempt || attempt.terminal) {
				return;
			}

			completePublishAttempt(attempt, 'cancelled', { outcome: 'unmounted' });
			attempt.disposable?.dispose();
		},
		[completePublishAttempt],
	);

	const handlePublishModalOpen = React.useCallback(() => {
		fireAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'migrationReviewPublishButton',
		});
		setIsPublishModalOpen(true);
	}, [fireAnalyticsEvent]);

	const handlePublishModalCancel = React.useCallback(() => {
		setIsPublishModalOpen(false);
	}, []);

	const handleFinishLaterModalOpen = React.useCallback(() => {
		fireAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dropdownItem',
			actionSubjectId: 'migrationReviewFinishLaterMenuItem',
		});
		fireAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'modal',
			actionSubjectId: 'migrationReviewFinishLaterModal',
		});
		setIsFinishLaterModalOpen(true);
	}, [fireAnalyticsEvent]);

	const handleFinishLaterModalCancel = React.useCallback(() => {
		fireAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'migrationReviewFinishLaterModalCancel',
		});
		setIsFinishLaterModalOpen(false);
	}, [fireAnalyticsEvent]);

	const handlePublish = () => {
		const publishAttemptState = publishAttemptRef.current;
		if (isPublishing || (publishAttemptState.attempt && !publishAttemptState.attempt.terminal)) {
			return;
		}

		const attempt: PublishAttempt = {
			disposable: null,
			startedAt: Date.now(),
			terminal: false,
			retry: publishAttemptState.retry,
		};
		publishAttemptState.attempt = attempt;
		setIsPublishModalOpen(false);
		attempt.disposable = commitGoLiveHelpCenter({
			variables: {
				cloudId,
				input: { helpCenterAri },
			},
			onCompleted: (response) => {
				const goLiveHelpCenter = response?.helpCenter?.goLiveHelpCenter;
				if (goLiveHelpCenter?.success) {
					if (completePublishAttempt(attempt, 'succeeded', { outcome: 'published' })) {
						window.location.href = getMigrationPublishedUrl(publishedHelpCenterUrl);
					}
					return;
				}

				if (
					completePublishAttempt(attempt, 'failed', {
						errorCode: goLiveHelpCenter ? 'PUBLISH_REJECTED' : 'EMPTY_RESPONSE',
					})
				) {
					showPublishErrorFlag();
				}
			},
			onError: () => {
				if (completePublishAttempt(attempt, 'failed', { errorCode: 'RELAY_REQUEST_FAILED' })) {
					showPublishErrorFlag();
				}
			},
		});
	};

	const handleMoreActionsClick = React.useCallback(() => {
		fireAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'migrationReviewMoreActionsButton',
		});
	}, [fireAnalyticsEvent]);

	const handleEditDraft = React.useCallback(() => {
		fireAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dropdownItem',
			actionSubjectId: 'migrationReviewEditDraftMenuItem',
		});
		migrationReview.onEditDraft();
	}, [fireAnalyticsEvent, migrationReview]);

	const handleFinishLaterConfirm = React.useCallback(() => {
		fireAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'migrationReviewFinishLaterModalConfirm',
		});
		migrationReview.onFinishLater();
		setIsFinishLaterModalOpen(false);
	}, [fireAnalyticsEvent, migrationReview]);

	return (
		<React.Fragment>
			{/* eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- the rule does not yet recognise @atlaskit/primitives subpath entry points */}
			<Box backgroundColor="color.background.accent.blue.subtlest" xcss={styles.container}>
				<Inline
					alignBlock="center"
					rowSpace="space.150"
					shouldWrap
					space="space.200"
					spread="space-between"
					testId="migration-review-action-bar"
				>
					<Inline alignBlock="center" space="space.100">
						<EyeOpenIcon label="" />
						<Text>{formatMessage(messages.banner)}</Text>
					</Inline>
					<Inline
						alignBlock="center"
						rowSpace="space.100"
						shouldWrap
						space="space.100"
						testId="migration-review-action-bar-actions"
					>
						<SplitButton appearance="primary">
							<Button
								isDisabled={isPublishing}
								isLoading={isPublishing}
								onClick={handlePublishModalOpen}
							>
								{formatMessage(messages.publish)}
							</Button>
							<DropdownMenu<HTMLButtonElement>
								shouldRenderToParent
								trigger={({ triggerRef, ...triggerProps }) => (
									<IconButton
										ref={triggerRef}
										{...triggerProps}
										onClickCapture={handleMoreActionsClick}
										icon={ChevronDownIcon}
										label={formatMessage(messages.morePublishActions)}
									/>
								)}
							>
								<DropdownItemGroup>
									<DropdownItem onClick={handleEditDraft}>
										{formatMessage(messages.editDraft)}
									</DropdownItem>
									<DropdownItem onClick={handleFinishLaterModalOpen}>
										{formatMessage(messages.finishLater)}
									</DropdownItem>
								</DropdownItemGroup>
							</DropdownMenu>
						</SplitButton>
					</Inline>
				</Inline>
			</Box>
			<PublishMigrationModal
				isOpen={isPublishModalOpen}
				onCancel={handlePublishModalCancel}
				onConfirm={handlePublish}
			/>
			<FinishMigrationReviewModal
				helpCenterName={helpCenterName}
				isOpen={isFinishLaterModalOpen}
				onCancel={handleFinishLaterModalCancel}
				onConfirm={handleFinishLaterConfirm}
			/>
		</React.Fragment>
	);
};
